/* eslint-disable react/prop-types */
/**
 * /settings/profile — every user.
 *
 * Avatar, password, session length, AI/workflow preferences, device
 * install, push subscriptions, changelog.
 */
import { useState } from "react";
import { KeyRound, Lock } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { EntityShell } from "../../components/shell";
import AvatarUploader from "../../components/AvatarUploader";
import TenantSettingsCard from "../../components/settings/TenantSettingsCard";
import BrandingCard from "../../components/settings/BrandingCard";
import VisionClassifyCard from "../../components/settings/VisionClassifyCard";
import NavigationPrefsCard from "../../components/settings/NavigationPrefsCard";
import NotificationSettingsCard from "../../components/settings/NotificationSettingsCard";
import UsageLogCard from "../../components/settings/UsageLogCard";
import {
  SessionLengthCard,
  DeviceAppCard,
  MenuCustomisationCard,
  HidroPreferencesCard,
  BriefCoachingCard,
} from "./_blocks";

export default function ProfileSettings() {
  const { user, setUser } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwBanner, setPwBanner] = useState("");

  const submitPw = async (e) => {
    e.preventDefault();
    setPwErr(""); setPwBanner("");
    if (pw.new_password.length < 4) return setPwErr("New password must be at least 4 characters.");
    if (pw.new_password !== pw.confirm) return setPwErr("New password and confirmation don't match.");
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      setPwBanner("Password updated. You'll use the new one next time you log in.");
      setTimeout(() => setPwBanner(""), 4000);
    } catch (e2) {
      setPwErr(formatErr(e2.response?.data?.detail) || "Couldn't update password.");
    }
  };

  return (
    <EntityShell kindLabel="Settings" title="Profile & access" backTo="/settings">
      <div className="space-y-4" data-testid="settings-profile-page">
        {/* My profile */}
        <div className="hv-card p-5" data-testid="my-profile-card">
          <h2 className="font-display text-2xl font-bold">My profile</h2>
          <p className="text-sm text-[#a0aabf] mt-1">
            <span className="text-[#dde3ec] font-medium">{user?.name}</span>
            {" · "}<span className="text-[#647087] break-all">{user?.email}</span>
          </p>
          <div className="mt-4 max-w-2xl">
            <AvatarUploader user={user} onChange={(newUrl) => setUser({ ...user, avatar_url: newUrl })} />
          </div>
        </div>

        {/* Change password */}
        <div className="hv-card p-5" data-testid="change-password-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#A78BFA]/15 border border-[#A78BFA]/40 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#A78BFA]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-2xl font-bold">Change my password</h2>
              <p className="text-sm text-[#a0aabf] mt-1">
                Signed in as <span className="text-[#dde3ec] font-medium">{user?.email}</span>. Pick a new password — minimum 4 characters.
              </p>
              <form onSubmit={submitPw} className="grid sm:grid-cols-3 gap-3 mt-4 max-w-3xl" data-testid="change-password-form">
                <input type="password" placeholder="Current password" value={pw.current_password}
                  onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                  className="hv-input" autoComplete="current-password" required data-testid="cp-current" />
                <input type="password" placeholder="New password" value={pw.new_password}
                  onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                  className="hv-input" autoComplete="new-password" required data-testid="cp-new" />
                <input type="password" placeholder="Confirm new password" value={pw.confirm}
                  onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                  className="hv-input" autoComplete="new-password" required data-testid="cp-confirm" />
                <div className="sm:col-span-3 flex items-center gap-3 flex-wrap">
                  <button type="submit" className="hv-btn hv-btn-primary" data-testid="cp-submit">
                    <Lock className="w-4 h-4" /> Update password
                  </button>
                  {pwErr && <div className="text-sm text-rose-300" data-testid="cp-error">{pwErr}</div>}
                  {pwBanner && <div className="text-sm text-emerald-300" data-testid="cp-success">{pwBanner}</div>}
                </div>
              </form>
            </div>
          </div>
        </div>

        <SessionLengthCard />
        <TenantSettingsCard />
        <DeviceAppCard />
        <NavigationPrefsCard />
        <NotificationSettingsCard />
        <MenuCustomisationCard />
        <HidroPreferencesCard />
        <VisionClassifyCard />
        <UsageLogCard userRole={user?.role} />
        <BriefCoachingCard />
        <div data-testid="branding-section">
          <BrandingCard />
        </div>
      </div>
    </EntityShell>
  );
}
